import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '../lib/api';
import { Task, User, WorkspaceGroup } from '../types';

interface TaskDetailPanelProps {
  task: Task;
  statusBadge: React.ReactNode;
  domainName: string;
  assigneeName: string;
  createdAtLabel: string;
  canClaim: boolean;
  canAct: boolean;
  // Reassign-only visibility: manager/superadmin may reassign without being
  // the assignee (backend TaskService.isPrivilegedRole). Complete stays
  // assignee-only via canAct — do NOT fold this into canAct.
  canReassign: boolean;
  members: User[];
  groups: WorkspaceGroup[];
  onClose: () => void;
  onActionSuccess: () => void;
  initialShowReassign?: boolean;
}

const TaskDetailPanel: React.FC<TaskDetailPanelProps> = ({
  task, statusBadge, domainName, assigneeName, createdAtLabel,
  canClaim, canAct, canReassign, members, groups, onClose, onActionSuccess, initialShowReassign
}) => {
  const { t } = useTranslation();
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showReassign, setShowReassign] = useState(!!initialShowReassign);
  const [reassignType, setReassignType] = useState<'user' | 'group'>('user');
  const [reassignId, setReassignId] = useState('');
  const [reassignNote, setReassignNote] = useState('');
  // Complete-with-note (backend requires body {note} min 2 chars)
  const [showCompleteNote, setShowCompleteNote] = useState(false);
  const [completeNote, setCompleteNote] = useState('');

  const handleClaim = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await api.put(`/tasks/${task.id}/claim`);
      onActionSuccess();
      onClose();
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, 'Failed to claim task.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!completeNote.trim() || completeNote.trim().length < 2) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.put(`/tasks/${task.id}/complete`, { note: completeNote.trim() });
      onActionSuccess();
      onClose();
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, 'Failed to complete task.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignId || !reassignNote.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const body = reassignType === 'user'
        ? { assigned_to_user_id: reassignId, note: reassignNote.trim() }
        : { assigned_to_group_id: reassignId, note: reassignNote.trim() };
      await api.put(`/tasks/${task.id}/reassign`, body);
      onActionSuccess();
      onClose();
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, 'Failed to reassign task.'));
    } finally {
      setActionLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div onClick={onClose} className="absolute inset-0 bg-black bg-opacity-40" />
      <aside className="relative w-full max-w-md h-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl flex flex-col overflow-y-auto">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <span className="font-mono text-sm text-gray-500 dark:text-gray-400">
            {task.project_key}-{task.task_number}
          </span>
          {statusBadge}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 flex-1">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{task.title}</h2>
            {task.description && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{task.description}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('tasks.board.domain')}</div>
              <div className="text-sm text-gray-900 dark:text-gray-100">{domainName}</div>
            </div>
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('common.assignedTo')}</div>
              <div className="text-sm text-gray-900 dark:text-gray-100">{assigneeName}</div>
            </div>
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700 col-span-2">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('common.createdAt')}</div>
              <div className="text-sm text-gray-900 dark:text-gray-100">{createdAtLabel}</div>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
          {actionError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm">
              {actionError}
            </div>
          )}

          {task.status === 'todo' && canClaim && (
            <button
              type="button"
              onClick={handleClaim}
              disabled={actionLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {actionLoading ? t('tasks.detail.actions.processing') : t('tasks.detail.actions.claim')}
            </button>
          )}

          {task.status === 'claimed' && canAct && (
            <>
              <button
                type="button"
                onClick={() => { setShowCompleteNote(true); setActionError(null); }}
                disabled={actionLoading}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? t('tasks.detail.actions.processing') : t('tasks.detail.actions.complete')}
              </button>

              {showCompleteNote && (
                <form
                  onSubmit={(e) => { e.preventDefault(); handleComplete(); }}
                  className="p-3 border border-green-200 dark:border-green-900/40 rounded-lg bg-green-50 dark:bg-green-900/20 space-y-3"
                >
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('tasks.detail.actions.completionNote')}</label>
                  <textarea
                    required
                    minLength={2}
                    rows={3}
                    value={completeNote}
                    onChange={(e) => setCompleteNote(e.target.value)}
                    placeholder={t('tasks.detail.actions.completionNotePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={actionLoading || !completeNote.trim() || completeNote.trim().length < 2}
                      className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {actionLoading ? t('tasks.detail.actions.processing') : t('tasks.detail.actions.complete')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowCompleteNote(false); setCompleteNote(''); setActionError(null); }}
                      disabled={actionLoading}
                      className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}

          {task.status === 'claimed' && canReassign && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => { setShowReassign((prev) => !prev); setActionError(null); }}
                disabled={actionLoading}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                {t('tasks.detail.actions.reassign')}
              </button>

              {showReassign && (
                <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/40 space-y-3">
                  <div className="flex gap-4">
                    <label className="flex items-center text-sm">
                      <input
                        type="radio"
                        name="panelReassignType"
                        value="user"
                        checked={reassignType === 'user'}
                        onChange={() => { setReassignType('user'); setReassignId(''); }}
                        className="mr-1"
                      />
                      {t('tasks.detail.actions.reassignToUser')}
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="radio"
                        name="panelReassignType"
                        value="group"
                        checked={reassignType === 'group'}
                        onChange={() => { setReassignType('group'); setReassignId(''); }}
                        className="mr-1"
                      />
                      {t('tasks.detail.actions.reassignToGroup')}
                    </label>
                  </div>

                  {reassignType === 'user' ? (
                    <select
                      value={reassignId}
                      onChange={(e) => setReassignId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('tasks.detail.actions.selectUser')}</option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={reassignId}
                      onChange={(e) => setReassignId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('tasks.detail.actions.selectGroup')}</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.detail.actions.reassignNote')} *</label>
                    <textarea
                      required
                      minLength={2}
                      rows={2}
                      value={reassignNote}
                      onChange={(e) => setReassignNote(e.target.value)}
                      placeholder={t('tasks.detail.actions.reassignNotePlaceholder') as string}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleReassign}
                      disabled={actionLoading || !reassignId || !reassignNote.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {actionLoading ? t('tasks.detail.actions.processing') : t('tasks.detail.actions.confirmReassign')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowReassign(false); setReassignId(''); setReassignNote(''); setActionError(null); }}
                      disabled={actionLoading}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {task.status === 'todo' && !canClaim && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.info.notAssigned')}</p>
          )}
          {task.status === 'claimed' && !canAct && !canReassign && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.info.takenByOther')}</p>
          )}
          {task.status === 'completed' && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.info.completed')}</p>
          )}

          <Link
            to={`/tasks/${task.id}`}
            className="block text-center text-sm text-blue-600 dark:text-blue-400 hover:underline pt-1"
          >
            {t('tasks.detail.viewFullDetails')}
          </Link>
        </div>
      </aside>
    </div>,
    document.body
  );
};

export default TaskDetailPanel;




